import React, { useState } from "react";
import { updateObligation, completeObligation, calculateRiskScore, calculateProgressPercentage } from "../../services/obligationService";
import { useApp } from "../../contexts/AppContext";

export const ObligationDetail = ({ obligation, onClose, onUpdate, canEdit }) => {
  const { service, addNotification } = useApp();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [milestones, setMilestones] = useState(obligation.milestones || []);
  const riskScore = calculateRiskScore(obligation);

  const dueDate = new Date(obligation.DueDate);
  const today = new Date();
  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  const handleToggleMilestone = async (index) => {
    const updated = [...milestones];
    updated[index].completed = !updated[index].completed;
    setMilestones(updated);

    try {
      setSaving(true);
      const percentage = calculateProgressPercentage(updated);
      await updateObligation(service, obligation.id, {
        MilestoneStatus: JSON.stringify(updated),
        ProgressPercentage: percentage,
      });
      addNotification("Milestone updated", "success");
      onUpdate();
    } catch (e) {
      addNotification("Failed to update milestone", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    try {
      setSaving(true);
      await completeObligation(service, obligation.id);
      addNotification("Obligation marked as complete", "success");
      onUpdate();
    } catch (e) {
      addNotification("Failed to complete obligation", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-5">
      <div className="bg-blue-50 border border-gray-200 rounded-lg max-w-2xl w-full max-h-screen overflow-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-blue-50 flex items-center justify-between">
          <div>
            <h2 className="m-0 text-gray-900 text-xl font-semibold">{obligation.Title}</h2>
            <p className="mt-1 text-gray-700 text-sm">Category: {obligation.Category}</p>
          </div>
          <button onClick={onClose} className="bg-none border-none text-gray-700 hover:text-gray-800 cursor-pointer text-2xl p-0 leading-none">
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Key Info */}
          <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-white rounded-lg">
            <div>
              <div className="text-xs text-gray-700 uppercase font-semibold mb-1">Status</div>
              <div className="text-sm text-gray-900 font-semibold">{obligation.Status}</div>
            </div>
            <div>
              <div className="text-xs text-gray-700 uppercase font-semibold mb-1">Priority</div>
              <div className="text-sm text-gray-900 font-semibold">{obligation.Priority}</div>
            </div>
            <div>
              <div className="text-xs text-gray-700 uppercase font-semibold mb-1">Due Date</div>
              <div className="text-sm text-gray-900 font-semibold">{dueDate.toLocaleDateString("en-GB")} ({daysUntilDue}d)</div>
            </div>
            <div>
              <div className="text-xs text-gray-700 uppercase font-semibold mb-1">Responsible</div>
              <div className="text-sm text-gray-900">{obligation.ResponsibleParty}</div>
            </div>
          </div>

          {/* Risk Assessment */}
          <div className={`mb-6 p-4 rounded-lg border ${
            riskScore.score >= 50 ? "bg-orange-600/20 border-orange-300" : "bg-green-50 border-green-300"
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className={`m-0 text-sm font-semibold ${
                riskScore.score >= 50 ? "text-orange-600" : "text-green-700"
              }`}>Risk Assessment</h4>
              <div className="text-right">
                <div className="text-xs text-gray-700">Risk Score</div>
                <div className={`text-2xl font-semibold ${
                  riskScore.score >= 50 ? "text-orange-600" : "text-green-700"
                }`}>{riskScore.score}/100</div>
              </div>
            </div>
            <div className="text-sm text-gray-900 mb-2">
              <strong>Trend:</strong> {riskScore.trend.toUpperCase()}
            </div>
            <div className="text-sm text-gray-900">
              <strong>Recommendation:</strong> {riskScore.recommendation}
            </div>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="m-0 text-gray-900 text-sm font-semibold">Progress</h4>
              <span className="text-sm font-semibold text-blue-600">{obligation.ProgressPercentage}%</span>
            </div>
            <div className="bg-white h-3 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full transition-all duration-300 ${
                  obligation.ProgressPercentage === 100 ? "bg-green-700" : "bg-orange-600"
                }`}
                style={{width: obligation.ProgressPercentage + "%"}}
              />
            </div>
            {obligation.EstimatedDays && (
              <div className="text-xs text-gray-700">
                Estimated: {obligation.EstimatedDays} days • Actual: {obligation.ActualDays} days
              </div>
            )}
          </div>

          {/* Milestones */}
          {milestones.length > 0 && (
            <div className="mb-6 p-4 bg-white rounded-lg">
              <h4 className="m-0 mb-3 text-gray-900 text-sm font-semibold">Milestones</h4>
              <div className="grid gap-2">
                {milestones.map((m, i) => (
                  <label key={i} className="flex items-center gap-2.5 p-2 bg-blue-50 rounded cursor-pointer text-gray-900">
                    <input
                      type="checkbox"
                      checked={m.completed}
                      onChange={() => handleToggleMilestone(i)}
                      disabled={!canEdit || saving}
                      className="accent-blue-600 cursor-pointer"
                    />
                    <span className={`flex-1 text-sm ${m.completed ? "line-through text-gray-700" : ""}`}>{m.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {obligation.Description && (
            <div className="mb-6 p-4 bg-white rounded-lg">
              <h4 className="m-0 mb-2 text-gray-900 text-sm font-semibold">Description</h4>
              <p className="m-0 text-gray-700 text-sm leading-relaxed">{obligation.Description}</p>
            </div>
          )}

          {/* Risk Details */}
          {obligation.RiskDescription && (
            <div className="mb-6 p-4 bg-orange-600/20 border border-orange-300 rounded-lg">
              <h4 className="m-0 mb-2 text-orange-600 text-sm font-semibold">What Could Go Wrong?</h4>
              <p className="m-0 text-gray-900 text-sm leading-relaxed">{obligation.RiskDescription}</p>
            </div>
          )}

          {/* Stakes */}
          {obligation.StakesDescription && (
            <div className="mb-6 p-4 bg-blue-600/20 border border-blue-300 rounded-lg">
              <h4 className="m-0 mb-2 text-blue-600 text-sm font-semibold">What's at Stake?</h4>
              <p className="m-0 text-gray-900 text-sm leading-relaxed">{obligation.StakesDescription}</p>
            </div>
          )}

          {/* Actions */}
          {canEdit && obligation.Status !== "Completed" && (
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleComplete}
                disabled={saving}
                className={`flex-1 px-4 py-2.5 bg-green-700 border-none rounded text-white cursor-pointer font-semibold ${
                  saving ? "opacity-50 cursor-not-allowed" : "hover:bg-green-600"
                }`}
              >
                {saving ? "Completing..." : "✓ Mark Complete"}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2.5 bg-white border border-gray-200 rounded text-gray-700 cursor-pointer font-semibold hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          )}

          {!canEdit && (
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded text-gray-700 cursor-pointer font-semibold hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
